"use client";

import { c as _c } from "react/compiler-runtime";
import { EllipsisVerticalIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { memo, useEffect, useRef, useState } from "react";
import { useClickAway } from "react-use";
import { getConnectionProfileIndicator } from "@/components/auth/connection-state-indicator";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import HeaderUserConnect from "@/components/header/user/HeaderUserConnect";
import HeaderUserProxyDropdown from "@/components/header/user/proxy/HeaderUserProxyDropdown";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import UserLevel from "@/components/user/utils/level/UserLevel";
import { DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP } from "@/constants/constants";
import { isAutoGeneratedHandle, isEthereumAddress } from "@/helpers/AllowlistToolHelpers";
import { useIdentity } from "@/hooks/useIdentity";
interface WebSidebarUserProps {
  readonly isCollapsed: boolean;
  readonly profile: any;
}
function WebSidebarUser(t0) {
  const $ = _c(63);
  const {
    isCollapsed,
    profile: parentProfile
  } = t0;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const {
    address,
    seizeConnect,
    isAuthenticated,
    isConnected,
    connectedAccounts,
    seizeSwitchConnectedAccount
  } = useSeizeConnectContext();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const profileClickTimeoutRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (profileClickTimeoutRef.current) {
        clearTimeout(profileClickTimeoutRef.current);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== showUserMenu) {
    t3 = event => {
      if (showUserMenu && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    $[2] = showUserMenu;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useClickAway(dropdownRef, t3);
  const t4 = address || "";
  let t5;
  if ($[4] !== t4) {
    t5 = {
      handleOrWallet: t4,
      initialProfile: null
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    profile: localProfile,
    isLoading
  } = useIdentity(t5);
  const profile = parentProfile || localProfile;
  let t6;
  if ($[6] !== profile) {
    t6 = profile?.pfp ? resolveIpfsUrlSync(profile.pfp) : null;
    $[6] = profile;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const resolvedPfp = t6;
  const [avatarSrc, setAvatarSrc] = useState(resolvedPfp ?? DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP);
  const isResolvedAvatar = !!resolvedPfp && avatarSrc === resolvedPfp;
  let t7;
  let t8;
  if ($[8] !== resolvedPfp) {
    t7 = () => {
      setAvatarSrc(resolvedPfp ?? DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP);
    };
    t8 = [resolvedPfp];
    $[8] = resolvedPfp;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  if (!address) {
    let t9;
    if ($[11] !== isCollapsed || $[12] !== seizeConnect) {
      t9 = <div className="tw-mt-auto tw-relative tw-w-full tw-group tw-pb-2 tw-px-3">{isCollapsed ? <button className="tw-block tw-h-12 tw-w-full tw-cursor-pointer tw-rounded-xl tw-border-none tw-bg-transparent tw-px-2 tw-text-base tw-font-medium tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-200 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-iron-500 focus-visible:tw-ring-offset-2 active:tw-bg-transparent desktop-hover:hover:tw-text-white" onClick={() => seizeConnect()} title="Connect Wallet" data-tooltip-id="sidebar-tooltip" data-tooltip-content="Connect Wallet"><div className="tw-flex tw-h-full tw-w-full tw-items-center"><div className="tw-flex tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center"><UserCircleIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></div></div></button> : <div className="tw-mt-2 tw-w-full tw-px-2"><div className="[&>button]:tw-w-full"><HeaderUserConnect /></div></div>}</div>;
      $[11] = isCollapsed;
      $[12] = seizeConnect;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  if (isLoading || !profile) {
    const t9 = `tw-flex tw-w-full tw-items-center tw-rounded-xl tw-px-2 tw-py-2 ${isCollapsed ? "tw-justify-center" : "tw-justify-start tw-gap-x-3"}`;
    let t10;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-animate-pulse tw-rounded-lg tw-bg-iron-800" />;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== isCollapsed) {
      t11 = !isCollapsed && <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-y-2"><div className="tw-h-4 tw-w-24 tw-animate-pulse tw-rounded tw-bg-iron-800" /><div className="tw-h-3 tw-w-16 tw-animate-pulse tw-rounded tw-bg-iron-800" /></div>;
      $[15] = isCollapsed;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== t11 || $[18] !== t9) {
      t12 = <div className="tw-mt-auto tw-relative tw-w-full tw-group tw-pb-2 tw-px-3"><div className={t9}>{t10}{t11}</div></div>;
      $[17] = t11;
      $[18] = t9;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    return t12;
  }
  const displayHandle = profile.handle || address || "";
  let t9;
  if ($[20] !== address) {
    t9 = address && (isEthereumAddress(address) || isAutoGeneratedHandle(address));
    $[20] = address;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const shouldTruncate = t9;
  let t10;
  if ($[22] !== isAuthenticated || $[23] !== isConnected) {
    t10 = getConnectionProfileIndicator({
      isAuthenticated,
      isConnected
    });
    $[22] = isAuthenticated;
    $[23] = isConnected;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const connectionIndicator = t10;
  let t11;
  if ($[25] !== connectedAccounts || $[26] !== seizeSwitchConnectedAccount) {
    t11 = () => {
      if (connectedAccounts.length < 2) {
        return false;
      }
      const activeIndex = connectedAccounts.findIndex(_temp);
      const currentIndex = Math.max(activeIndex, 0);
      const nextAccount = connectedAccounts[(currentIndex + 1) % connectedAccounts.length];
      if (!nextAccount) {
        return false;
      }
      ;
      try {
        seizeSwitchConnectedAccount(nextAccount.address);
        return true;
      } catch (t12) {
        const error = t12;
        console.error("Failed to switch connected account", error);
        return false;
      }
    };
    $[25] = connectedAccounts;
    $[26] = seizeSwitchConnectedAccount;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const switchToNextConnectedAccount = t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setShowUserMenu(_temp2);
    };
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const toggleUserMenu = t12;
  let t13;
  if ($[29] !== switchToNextConnectedAccount) {
    t13 = () => {
      if (profileClickTimeoutRef.current) {
        clearTimeout(profileClickTimeoutRef.current);
        profileClickTimeoutRef.current = null;
        const didSwitchAccount = switchToNextConnectedAccount();
        if (!didSwitchAccount) {
          toggleUserMenu();
        }
        return;
      }
      profileClickTimeoutRef.current = setTimeout(() => {
        profileClickTimeoutRef.current = null;
        toggleUserMenu();
      }, 280);
    };
    $[29] = switchToNextConnectedAccount;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const onProfileActivate = t13;
  const t14 = `tw-group/user tw-mt-1 tw-w-full tw-rounded-xl tw-border-none tw-bg-transparent tw-px-2 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors tw-duration-200 ${isCollapsed ? "" : "desktop-hover:hover:tw-bg-iron-900"}`;
  const t15 = `tw-flex tw-w-full tw-items-center ${isCollapsed ? "" : "tw-gap-x-2"}`;
  const t16 = `tw-relative tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl ${connectionIndicator.avatarClassName}`;
  const t17 = displayHandle || "Connected account";
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = event_0 => {
      if (event_0.currentTarget.src.endsWith(DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP)) {
        return;
      }
      setAvatarSrc(DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP);
    };
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = `tw-absolute tw-inset-0 tw-block tw-h-full tw-w-full tw-rounded-xl tw-bg-iron-900 ${isResolvedAvatar ? "tw-object-contain" : "tw-object-cover tw-grayscale"}`;
  let t20;
  if ($[32] !== avatarSrc || $[33] !== t17 || $[34] !== t19) {
    t20 = <img src={avatarSrc} alt={t17} onError={t18} className={t19} />;
    $[32] = avatarSrc;
    $[33] = t17;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== connectionIndicator.overlayClassName) {
    t21 = connectionIndicator.overlayClassName && <div className={`tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-xl ${connectionIndicator.overlayClassName}`} />;
    $[36] = connectionIndicator.overlayClassName;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== connectionIndicator.title || $[39] !== t16 || $[40] !== t20 || $[41] !== t21) {
    t22 = <div className={t16} title={connectionIndicator.title}>{t20}{t21}</div>;
    $[38] = connectionIndicator.title;
    $[39] = t16;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== displayHandle || $[44] !== isCollapsed || $[45] !== profile.level || $[46] !== shouldTruncate) {
    t23 = !isCollapsed && <><div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start tw-justify-between tw-whitespace-nowrap"><div className={`-tw-mt-0.5 tw-mb-1 tw-w-full tw-overflow-hidden tw-text-left tw-text-md tw-font-medium tw-leading-tight tw-text-white ${shouldTruncate ? "tw-max-w-[8rem] tw-truncate" : ""}`}>{displayHandle}</div><UserLevel level={profile.level} size="xxs" asSpan={true} /></div><EllipsisVerticalIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-400 group-hover/user:tw-text-white" /></>;
    $[43] = displayHandle;
    $[44] = isCollapsed;
    $[45] = profile.level;
    $[46] = shouldTruncate;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t15 || $[49] !== t22 || $[50] !== t23) {
    t24 = <div className={t15}>{t22}{t23}</div>;
    $[48] = t15;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== onProfileActivate || $[53] !== showUserMenu || $[54] !== t14 || $[55] !== t24) {
    t25 = <button ref={buttonRef} onClick={onProfileActivate} className={t14} aria-label="Open user menu" aria-expanded={showUserMenu} aria-controls="user-menu">{t24}</button>;
    $[52] = onProfileActivate;
    $[53] = showUserMenu;
    $[54] = t14;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== profile || $[58] !== showUserMenu) {
    t26 = showUserMenu && profile && <div ref={dropdownRef}><HeaderUserProxyDropdown profile={profile} isOpen={showUserMenu} onClose={() => setShowUserMenu(false)} /></div>;
    $[57] = profile;
    $[58] = showUserMenu;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t25 || $[61] !== t26) {
    t27 = <div className="tw-mt-auto tw-relative tw-w-full tw-group tw-pb-2 tw-px-3">{t25}{t26}</div>;
    $[60] = t25;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  return t27;
}
function _temp2(current) {
  return !current;
}
function _temp(account) {
  return account.isActive;
}
export default memo(WebSidebarUser);